import { IInputProps } from '@cian/ui-kit/input/types';
import { useCallback, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { setValidationError } from '../../../actions/rosreestrData';
import { setRequiredError } from '../../../actions/rosreestrData/setRequiredError';
import { updateOffer } from '../../../redux/reducers';
import { selectCadastralNumbers } from '../../../selectors/offer/selectCadastralNumbers';
import { selectCadastralFieldsMeta } from '../../../selectors/rosreestrData';
import { ECadastralFieldsNames, EErrorType, ICadastralField } from '../../../types/reducers/rosreestrData';
import { trackInvalidSymbolEntered, trackInvalidValueEntered, trackValidValueEntered } from '../tracking';
import { IAngularProps } from '../types';
import { getField, isValidValue, matchToCadastralMask } from '../utils';
import { useCadastralNumbersSaveHandler } from './useCadastralNumbersSaveHandler';
import { useValidateOnLoad } from './useValidateOnLoad';
import { useValidateOnSubmit } from './useValidateOnSubmit';

interface IParams extends IAngularProps {
  setShowNoCadastralNumberButton(value: boolean): void;
}

interface IUseCadastralFields extends Record<ECadastralFieldsNames, ICadastralField> {
  onChange: (key: string) => IInputProps['onChange'];
  requestError?: string;
}

/** Состояние и управление кадастровыми полями */
export const useCadastralFields = ({
  addFormService,
  $rootScope,
  setShowNoCadastralNumberButton,
}: IParams): IUseCadastralFields => {
  const dispatch = useDispatch();
  const cadastralFields = useSelector(selectCadastralNumbers, shallowEqual);
  const { buildingCadastralNumber = '', cadastralNumber = '' } = cadastralFields;
  const cadastralFieldsMeta = useSelector(selectCadastralFieldsMeta);

  const updateField = useCallback(
    (key: string, value: string) => {
      addFormService.setValueByPath(key, value);

      dispatch(updateOffer({ [key]: value || undefined }));
    },
    [addFormService, dispatch],
  );

  const validateField = useCallback(
    (name: ECadastralFieldsNames, value: string) => {
      const { isRequired, errors } = cadastralFieldsMeta[name];

      /** Проверяем, что поле обязательное и данные введены */
      if (isRequired) {
        if (!value) {
          dispatch(setRequiredError({ key: name, value: true }));
        } else if (errors[EErrorType.RequiredError].value) {
          dispatch(setRequiredError({ key: name, value: false }));
        }
      }

      /** Проверяем, что введены корректные символы */
      if (value && !isValidValue(value)) {
        const prevValue = cadastralFields[name] || '';

        /**
         * Позволяем печатать, пока предыдущее значение не станет валидным.
         * Это кейс, когда при редактировании объявления уже были невалидные кадастровые номера.
         */
        if (!prevValue || isValidValue(prevValue)) {
          trackInvalidSymbolEntered();

          return { name, value: prevValue };
        }
      }

      /** Проверяем, что значение соответствует кадастровой маске, иначе ошибка валидации */
      if (matchToCadastralMask(value)) {
        /* istanbul ignore else */ if (errors[EErrorType.ValidationError].value) {
          trackValidValueEntered(name);

          dispatch(setValidationError({ key: name, value: '' }));
        }
      } /* istanbul ignore else */ else if (value) {
        trackInvalidValueEntered();

        dispatch(setValidationError({ key: name, value }));
      }

      return { name, value };
    },
    [cadastralFields, cadastralFieldsMeta, dispatch],
  );

  const handleChange = useCallback(
    (key: ECadastralFieldsNames) => (_: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
      const { name, value } = validateField(key, newValue);

      updateField(name, value);
    },
    [updateField, validateField],
  );

  useCadastralNumbersSaveHandler({ setShowNoCadastralNumberButton });

  useValidateOnLoad({ validateField });

  useValidateOnSubmit({ $rootScope, validateField, setShowNoCadastralNumberButton });

  return useMemo(
    () => ({
      buildingCadastralNumber: getField({
        value: buildingCadastralNumber,
        ...cadastralFieldsMeta.buildingCadastralNumber,
      }),
      cadastralNumber: getField({
        value: cadastralNumber,
        ...cadastralFieldsMeta.cadastralNumber,
      }),
      onChange: handleChange,
    }),
    [buildingCadastralNumber, cadastralFieldsMeta, cadastralNumber, handleChange],
  );
};
